import { useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { ensureAdminBootstrap } from "@/lib/admin-bootstrap.functions";

export type AppRole = "client" | "chef" | "admin";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const loadRoles = async (uid: string) => {
      if (!mounted) return;
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid);
      if (mounted) setRoles((data ?? []).map((r) => r.role as AppRole));
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        setTimeout(() => {
          if (!mounted) return;
          // Bootstrap idempotente del ruolo admin (solo per ADMIN_EMAIL),
          // poi ricarica i ruoli per riflettere subito il cambio.
          void ensureAdminBootstrap()
            .catch(() => undefined)
            .finally(() => { if (mounted) void loadRoles(u.id); });
        }, 0);
      } else {
        setRoles([]);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const u = data.session?.user ?? null;
      setUser(u);
      if (u) {
        void ensureAdminBootstrap()
          .catch(() => undefined)
          .finally(() => { if (mounted) void loadRoles(u.id); });
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return {
    user,
    roles,
    loading,
    isChef: roles.includes("chef"),
    isAdmin: roles.includes("admin"),
    signOut: () => supabase.auth.signOut(),
  };
}